import type { IntlShape } from 'react-intl';
import type { AppRunExecution, AppRunSetup, AppRunnerReadiness, AppSkillRef } from '@api/apps';
import { agentDisplayName } from '@constants/agentNames';
import { APPROVAL_MODE_DEFAULT, getStoredApprovalMode } from '@utils/approvalMode';
import { CLOUD_RUNNER } from './runSetup.constants';

/** Why Run is refused before it is pressed; mirrors what the server would refuse. */
export type RunSetupBlocker =
  | { readonly kind: 'skillProblem'; readonly message: string }
  | { readonly kind: 'noRunner' }
  | { readonly kind: 'offline'; readonly runner: AppRunnerReadiness }
  | { readonly kind: 'outdated'; readonly runner: AppRunnerReadiness }
  | { readonly kind: 'missingSoftware'; readonly runner: AppRunnerReadiness; readonly missing: readonly AppSkillRef[] };

export const skillLabel = (skill: AppSkillRef): string =>
  skill.id.startsWith('custom:') ? skill.name : agentDisplayName(skill.name);

const isReady = (runner: AppRunnerReadiness): boolean => !runner.problem;

/** What the person picked, completed with the app's defaults wherever they picked nothing usable. */
export const resolveSelection = (setup: AppRunSetup, picked: Partial<AppRunExecution>): AppRunExecution => {
  const pickedRunnerExists =
    picked.runner === CLOUD_RUNNER
      ? setup.runsOn !== 'LOCAL'
      : setup.runners.some((runner) => runner.vmId === picked.runner);
  const fallbackRunner =
    setup.runsOn === 'LOCAL'
      ? (setup.runners.find(isReady) ?? setup.runners[0])?.vmId ?? ''
      : CLOUD_RUNNER;
  const runner = pickedRunnerExists && picked.runner ? picked.runner : fallbackRunner;

  const unavailable = setup.runners.find((candidate) => candidate.vmId === runner)?.unavailableAlternatives ?? [];
  const alternatives = setup.alternatives.map((skill) => skill.id);
  const usable = alternatives.filter((id) => !unavailable.includes(id));
  const skill =
    picked.skill && alternatives.includes(picked.skill) ? picked.skill : (usable[0] ?? alternatives[0]);

  return {
    runner,
    approvalMode: picked.approvalMode ?? getStoredApprovalMode(null) ?? APPROVAL_MODE_DEFAULT,
    skill,
  };
};

export const blockerOf = (setup: AppRunSetup, selection: AppRunExecution): RunSetupBlocker | null => {
  if (setup.skillProblem) return { kind: 'skillProblem', message: setup.skillProblem };
  if (selection.runner === CLOUD_RUNNER) return null;
  const runner = setup.runners.find((candidate) => candidate.vmId === selection.runner);
  if (!runner) return { kind: 'noRunner' };
  if (runner.problem === 'OFFLINE') return { kind: 'offline', runner };
  if (runner.problem === 'OUTDATED') return { kind: 'outdated', runner };
  const missingChoice = setup.alternatives.find(
    (skill) => skill.id === selection.skill && runner.unavailableAlternatives.includes(skill.id),
  );
  const missing = missingChoice ? [...runner.missing, missingChoice] : runner.missing;
  return missing.length > 0 ? { kind: 'missingSoftware', runner, missing } : null;
};

export const blockerMessage = (blocker: RunSetupBlocker, intl: IntlShape): string => {
  switch (blocker.kind) {
    case 'skillProblem':
      return blocker.message;
    case 'noRunner':
      return intl.formatMessage({
        id: 'apps.run.setup.noRunner',
        defaultMessage: 'This app runs on your computer. Connect a local runner in Settings, then run it.',
      });
    case 'offline':
      return intl.formatMessage(
        {
          id: 'apps.run.setup.offline',
          defaultMessage: 'Your runner {runner} is offline. Start it, then run the app.',
        },
        { runner: blocker.runner.name },
      );
    case 'outdated':
      return intl.formatMessage(
        {
          id: 'apps.run.setup.outdated',
          defaultMessage: 'Your runner {runner} needs updating before it can run apps. Download the latest runner.',
        },
        { runner: blocker.runner.name },
      );
    case 'missingSoftware':
      return intl.formatMessage(
        {
          id: 'apps.run.setup.missingSoftware',
          defaultMessage:
            "{runner} can't find {software}. Install it (or update the runner), restart the runner, then run the app.",
        },
        { runner: blocker.runner.name, software: blocker.missing.map(skillLabel).join(', ') },
      );
    default: {
      const unhandled: never = blocker;
      throw new Error(`Unhandled blocker: ${JSON.stringify(unhandled)}`);
    }
  }
};
